<template>
  <div class="space-y-6">
    <div class="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-6">
      <h3 class="text-lg font-medium font-poppins text-gray-900 dark:text-gray-100 mb-4">
        Hosting & Platform
      </h3>
      <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
        <UiControl id="hosting-platform" label="Website Platform">
          <UiInput v-model="form.website_platform" placeholder="e.g. WordPress, Wix, Squarespace" />
        </UiControl>
        <UiControl id="hosting-nameserver" label="Nameserver">
          <UiInput v-model="form.nameserver" placeholder="e.g. Cloudflare, GoDaddy" />
        </UiControl>
        <UiControl id="hosting-gf" label="GF Hosting?">
          <UiRadioGroup
            id="hosting-gf-radio"
            v-model="form.gf_hosting"
            :options="[{ value: 'yes', label: 'Yes' }, { value: 'no', label: 'No' }]"
          />
        </UiControl>
        <UiControl id="hosting-name" label="Host Name">
          <UiInput v-model="form.host_name" placeholder="e.g. SiteGround, Bluehost" />
        </UiControl>
        <UiControl id="hosting-login-url" label="Hosting Login URL">
          <UiInput v-model="form.hosting_login_url" placeholder="https://..." />
        </UiControl>
        <UiControl id="hosting-support-pin" label="Hosting Support Pin">
          <UiInput v-model="form.hosting_support_pin" placeholder="Support PIN" />
        </UiControl>
      </div>
    </div>

    <!-- Registrar Credentials -->
    <div class="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700">
      <button
        type="button"
        class="flex items-center justify-between w-full p-6 text-left"
        @click="toggleSection('registrar')"
      >
        <h3 class="text-lg font-medium font-poppins text-gray-900 dark:text-gray-100">
          Registrar Credentials
        </h3>
        <svg
          class="h-5 w-5 text-gray-400 transition-transform"
          :class="{ 'rotate-180': openSections.registrar }"
          fill="none" viewBox="0 0 24 24" stroke="currentColor"
        >
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7" />
        </svg>
      </button>
      <div v-if="openSections.registrar" class="px-6 pb-6">
        <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
          <UiControl id="registrar-label" label="Label">
            <UiInput v-model="credentials.registrar.label" placeholder="e.g. GoDaddy" />
          </UiControl>
          <UiControl id="registrar-login-url" label="Login URL">
            <UiInput v-model="credentials.registrar.login_url" placeholder="https://..." />
          </UiControl>
          <UiControl id="registrar-username" label="Username">
            <UiInput v-model="credentials.registrar.username" placeholder="Username" />
          </UiControl>
          <UiControl id="registrar-password" label="Password">
            <UiInput v-model="credentials.registrar.password" type="password" placeholder="Password" />
          </UiControl>
          <UiControl id="registrar-pin" label="PIN">
            <UiInput v-model="credentials.registrar.pin" placeholder="PIN" />
          </UiControl>
        </div>
      </div>
    </div>

    <!-- Cloudflare Credentials -->
    <div class="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700">
      <button
        type="button"
        class="flex items-center justify-between w-full p-6 text-left"
        @click="toggleSection('cloudflare')"
      >
        <h3 class="text-lg font-medium font-poppins text-gray-900 dark:text-gray-100">
          Cloudflare Credentials
        </h3>
        <svg
          class="h-5 w-5 text-gray-400 transition-transform"
          :class="{ 'rotate-180': openSections.cloudflare }"
          fill="none" viewBox="0 0 24 24" stroke="currentColor"
        >
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7" />
        </svg>
      </button>
      <div v-if="openSections.cloudflare" class="px-6 pb-6">
        <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
          <UiControl id="cloudflare-username" label="Username">
            <UiInput v-model="credentials.cloudflare.username" placeholder="Username or email" />
          </UiControl>
          <UiControl id="cloudflare-password" label="Password">
            <UiInput v-model="credentials.cloudflare.password" type="password" placeholder="Password" />
          </UiControl>
        </div>
      </div>
    </div>

    <!-- Site Access Credentials -->
    <div class="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700">
      <button
        type="button"
        class="flex items-center justify-between w-full p-6 text-left"
        @click="toggleSection('site_access')"
      >
        <h3 class="text-lg font-medium font-poppins text-gray-900 dark:text-gray-100">
          Site Access Credentials
        </h3>
        <svg
          class="h-5 w-5 text-gray-400 transition-transform"
          :class="{ 'rotate-180': openSections.site_access }"
          fill="none" viewBox="0 0 24 24" stroke="currentColor"
        >
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7" />
        </svg>
      </button>
      <div v-if="openSections.site_access" class="px-6 pb-6">
        <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
          <UiControl id="site-access-link" label="Login Link">
            <UiInput v-model="credentials.site_access.link" placeholder="https://example.com/wp-admin" />
          </UiControl>
          <UiControl id="site-access-username" label="Username">
            <UiInput v-model="credentials.site_access.username" placeholder="Username" />
          </UiControl>
          <UiControl id="site-access-password" label="Password">
            <UiInput v-model="credentials.site_access.password" type="password" placeholder="Password" />
          </UiControl>
        </div>
      </div>
    </div>

    <p v-if="mode === 'client'" class="text-xs text-gray-400 dark:text-gray-500 italic">
      Your credentials are encrypted and stored securely.
    </p>
  </div>
</template>

<script setup>
import { ref, reactive, onMounted } from 'vue'
import UiControl from '~/javascript/components/ui/form/control/control.vue'
import UiInput from '~/javascript/components/ui/form/input/input.vue'
import UiRadioGroup from '~/javascript/components/ui/form/radio/radio_group.vue'

const props = defineProps({
  stepKey: { type: String, required: true },
  label: { type: String, required: true },
  onboarding: { type: Object, required: true },
  mode: { type: String, required: true }
})

const openSections = reactive({
  registrar: false,
  cloudflare: false,
  site_access: false
})

function toggleSection(key) {
  openSections[key] = !openSections[key]
}

const form = ref({
  website_platform: '',
  nameserver: '',
  gf_hosting: '',
  host_name: '',
  hosting_login_url: '',
  hosting_support_pin: ''
})

const credentials = reactive({
  registrar: { label: '', login_url: '', username: '', password: '', pin: '' },
  cloudflare: { username: '', password: '' },
  site_access: { link: '', username: '', password: '' }
})

function findCredential(type) {
  return (props.onboarding.credentials || []).find(c => c.credential_type === type) || {}
}

onMounted(() => {
  const d = props.onboarding.seo_detail || {}
  form.value.website_platform = d.website_platform || ''
  form.value.nameserver = d.nameserver || ''
  form.value.gf_hosting = d.gf_hosting || ''
  form.value.host_name = d.host_name || ''
  form.value.hosting_login_url = d.hosting_login_url || ''
  form.value.hosting_support_pin = d.hosting_support_pin || ''

  const reg = findCredential('registrar')
  credentials.registrar.label = reg.label || ''
  credentials.registrar.login_url = reg.login_url || ''
  credentials.registrar.username = reg.username || ''
  credentials.registrar.password = ''
  credentials.registrar.pin = reg.pin || ''

  const cf = findCredential('cloudflare')
  credentials.cloudflare.username = cf.username || ''
  credentials.cloudflare.password = ''

  const sa = findCredential('site_access')
  credentials.site_access.link = sa.link || sa.login_url || ''
  credentials.site_access.username = sa.username || ''
  credentials.site_access.password = ''
})

defineExpose({
  getData: () => ({
    ...form.value,
    credentials: [
      { credential_type: 'registrar', ...credentials.registrar },
      { credential_type: 'cloudflare', ...credentials.cloudflare },
      { credential_type: 'site_access', ...credentials.site_access }
    ]
  })
})
</script>
